/**
 * SubmissionListV2 - Main Submissions List Page
 *
 * Refactored to follow SOLID principles:
 * - Single Responsibility: Each hook/component handles one concern
 * - Open/Closed: Easy to extend with new filters/operations
 * - Dependency Inversion: Uses abstractions (hooks) not implementations
 *
 * Architecture:
 * - State: Jotai atoms (store/)
 * - Data: Custom hooks with cursor-based pagination (hooks/)
 * - UI: Reusable components (components/)
 *
 * Pagination Strategy:
 * - Server-side cursor pagination for scalability (17x faster than offset)
 * - Client-side filtering for report status (cached reportsData)
 * - "Load More" pattern for progressive data loading
 */

import { useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom, useSetAtom } from "jotai";
import { toast } from "sonner";
import { Download, RefreshCw } from "lucide-react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Hooks
import { useJotFormData } from "@/hooks/useJotFormData";
import { useSubmissionExport } from "@/hooks/useSubmissionExport";
import { useAvailableTemplates } from "./hooks/useAvailableTemplates";
import { useLaunchFilter } from "./hooks/useLaunchFilter";
import { useSubmissionFilters } from "./hooks/useSubmissionFilters";
import { useBatchSelection } from "./hooks/useBatchSelection";
import { useSubmissionStats } from "./hooks/useSubmissionStats";
import { useBatchOperations } from "./hooks/useBatchOperations";
import { useSubmissionsPaginated } from "./hooks/useSubmissionsPaginated";

// Components
import { SubmissionSkeleton } from "./components/SubmissionSkeleton";
import { SubmissionFilters } from "./components/SubmissionFilters";
import { SubmissionStatsCards } from "./components/SubmissionStatsCards";
import { SubmissionTable } from "./components/SubmissionTable";
import { BatchActionsPanel } from "./components/BatchActionsPanel";
import { BatchDialogs } from "./components/BatchDialogs";

// Store
import {
  selectedFormAtom,
  pageSizeAtom,
  currentPageAtom,
  batchDialogAtom,
  openBatchDialogAtom,
  closeBatchDialogAtom,
  setBatchTemplateAtom,
  setBatchNotifyAtom,
} from "./store";
import type { SubmissionItem } from "./store/types";

const SubmissionListV2 = () => {
  const navigate = useNavigate();

  // =========================================================================
  // Atoms
  // =========================================================================
  const [selectedForm, setSelectedForm] = useAtom(selectedFormAtom);
  const [pageSize, setPageSize] = useAtom(pageSizeAtom);
  const [currentPage, setCurrentPage] = useAtom(currentPageAtom);
  const [batchDialog] = useAtom(batchDialogAtom);
  const openDialog = useSetAtom(openBatchDialogAtom);
  const closeDialog = useSetAtom(closeBatchDialogAtom);
  const setBatchTemplate = useSetAtom(setBatchTemplateAtom);
  const setBatchNotify = useSetAtom(setBatchNotifyAtom);

  // =========================================================================
  // Data Hooks
  // =========================================================================
  const { forms: jotFormForms, isLoadingForms } = useJotFormData();
  const { exportToCSV, isExporting } = useSubmissionExport();

  const {
    launches,
    selectedLaunchId,
    setSelectedLaunchId,
    selectedLaunchCodes,
  } = useLaunchFilter();

  const {
    filters,
    setFilter,
    setDateFilter,
    setReportStatus,
    clearFilters,
    hasActiveFilters,
    filterSubmissions,
  } = useSubmissionFilters();

  const {
    submissions: rawSubmissions,
    totalCount,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    sync,
    isSyncing,
  } = useSubmissionsPaginated({
    formId: selectedForm,
    enabled: !!selectedForm,
  });

  const { templates } = useAvailableTemplates({
    jotformFormId: selectedForm || null,
    enabled: !!selectedForm,
  });

  // =========================================================================
  // Selection Hook
  // =========================================================================
  const {
    selectedIds,
    selectedCount,
    hasSelection,
    toggleSelection,
    toggleSelectAll,
    clearSelection,
    isSelected,
    getSelectAllState,
  } = useBatchSelection();

  // =========================================================================
  // Derived Data
  // =========================================================================
  const submissions = rawSubmissions as SubmissionItem[];

  const { stats, reportsData, selectedHaveAnyReport } = useSubmissionStats({
    submissions: useMemo(
      () => filterSubmissions(submissions, selectedLaunchCodes, undefined),
      [submissions, selectedLaunchCodes, filterSubmissions]
    ),
    selectedIds,
  });

  // Filter submissions with reports data
  const filteredSubmissions = useMemo(
    () => filterSubmissions(submissions, selectedLaunchCodes, reportsData),
    [submissions, selectedLaunchCodes, reportsData, filterSubmissions]
  );

  // Pagination
  const totalPages = Math.ceil(filteredSubmissions.length / pageSize);
  const paginatedSubmissions = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredSubmissions.slice(startIndex, startIndex + pageSize);
  }, [filteredSubmissions, currentPage, pageSize]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedForm, selectedLaunchId, filters.reportStatus, setCurrentPage]);

  // =========================================================================
  // Batch Operations Hook
  // =========================================================================
  const {
    handleBatchGenerate,
    handleBulkDownload,
    handleBulkEmail,
    isGenerating,
    isDownloading,
    isSendingEmails,
    downloadProgress,
    selectedReportsData,
  } = useBatchOperations({
    selectedIds,
    templateId: batchDialog.templateId,
    onOperationComplete: () => {
      closeDialog();
      clearSelection();
    },
  });

  // =========================================================================
  // Handlers
  // =========================================================================
  const handleFormSelect = (formId: string) => {
    setSelectedForm(formId);
    setTimeout(() => {
      if (sync) {
        toast.info("Sincronizando submissions desde JotForm...");
        sync();
      }
    }, 500);
  };

  const handleExport = async () => {
    if (!selectedForm) {
      toast.error("Por favor selecciona un formulario");
      return;
    }
    const selectedFormData = jotFormForms?.find((f) => f.id === selectedForm);
    await exportToCSV({
      formId: selectedForm,
      formTitle: selectedFormData?.title,
      limit: 1000,
      offset: 0,
    });
  };

  const handleRowClick = (submission: SubmissionItem) => {
    navigate(`/admin/submissions/${submission.id}?formId=${selectedForm}`);
  };

  // =========================================================================
  // Render
  // =========================================================================
  const availableForms =
    jotFormForms?.filter((form) => form.status === "ENABLED") || [];

  if (isLoadingForms) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-64" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-10 w-[250px]" />
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-10 w-36" />
          </div>
        </div>
        <SubmissionSkeleton />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-800">Envíos</h1>
          <div className="flex items-center gap-2">
            <Select value={selectedForm} onValueChange={handleFormSelect}>
              <SelectTrigger className="w-[250px]">
                <SelectValue placeholder="Seleccionar formulario" />
              </SelectTrigger>
              <SelectContent>
                {availableForms.map((form) => (
                  <SelectItem key={form.id} value={form.id}>
                    {form.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              onClick={() => sync?.()}
              variant="outline"
              disabled={!selectedForm || isSyncing}
            >
              <RefreshCw
                size={16}
                className={isSyncing ? "animate-spin mr-2" : "mr-2"}
              />
              {isSyncing ? "Sincronizando..." : "Sincronizar"}
            </Button>

            <Button onClick={handleExport} disabled={!selectedForm || isExporting}>
              <Download size={16} className="mr-2" />
              {isExporting ? "Exportando..." : "Exportar a CSV"}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <SubmissionFilters
          filters={filters}
          launches={launches}
          selectedLaunchId={selectedLaunchId}
          onFilterChange={setFilter}
          onDateChange={setDateFilter}
          onLaunchChange={setSelectedLaunchId}
          onClearFilters={clearFilters}
          hasActiveFilters={hasActiveFilters}
        />

        {/* Content */}
        {!selectedForm ? (
          <div className="text-center py-8 text-muted-foreground">
            Selecciona un formulario para ver los envíos
          </div>
        ) : isLoading ? (
          <SubmissionSkeleton />
        ) : filteredSubmissions.length > 0 ? (
          <>
            {/* Stats + Batch Actions */}
            <div className="flex gap-4 transition-all duration-300">
              <SubmissionStatsCards
                stats={stats}
                activeFilter={filters.reportStatus}
                onFilterClick={setReportStatus}
                hasSelection={hasSelection}
                serverTotal={totalCount}
              />

              {hasSelection && (
                <BatchActionsPanel
                  selectedCount={selectedCount}
                  hasAnyReport={selectedHaveAnyReport}
                  isGenerating={isGenerating}
                  onDownloadClick={() => openDialog("download")}
                  onEmailClick={() => openDialog("email")}
                  onGenerateClick={() => openDialog("generate")}
                />
              )}
            </div>

            {/* Table */}
            <SubmissionTable
              submissions={paginatedSubmissions}
              totalCount={filteredSubmissions.length}
              currentPage={currentPage}
              pageSize={pageSize}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
              onRowClick={handleRowClick}
              isSelected={isSelected}
              onToggleSelection={toggleSelection}
              selectAllState={getSelectAllState(paginatedSubmissions)}
              onToggleSelectAll={() => toggleSelectAll(paginatedSubmissions)}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              onLoadMore={fetchNextPage}
              loadedCount={submissions.length}
              serverTotalCount={totalCount}
            />

            {/* Dialogs */}
            <BatchDialogs
              openDialog={batchDialog.open}
              onClose={closeDialog}
              selectedCount={selectedCount}
              hasAnyReport={selectedHaveAnyReport}
              templates={templates}
              templateId={batchDialog.templateId}
              onTemplateChange={setBatchTemplate}
              notify={batchDialog.notify}
              onNotifyChange={setBatchNotify}
              selectedReportsData={selectedReportsData}
              isGenerating={isGenerating}
              isDownloading={isDownloading}
              isSendingEmails={isSendingEmails}
              downloadProgress={downloadProgress}
              onGenerate={() => handleBatchGenerate(batchDialog.notify)}
              onDownload={handleBulkDownload}
              onSendEmails={handleBulkEmail}
            />
          </>
        ) : (
          <div className="text-center py-8">
            No hay envíos que coincidan con los filtros.
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default SubmissionListV2;
